import * as xlsx from 'xlsx';

/**
 * Hook utilitario abstraído para importar archivos de Excel de bitácoras de Lyta / Cryo.
 * Se encarga de leer el archivo, brincar los encabezados combinados (merged cells) 
 * y convertir cada fila en un objeto de Javascript mapeado correctamente.
 *
 * Mejorado: Detecta múltiples "tablas" dentro de una misma hoja de cálculo y
 * permite extraer solo la que se seleccione.
 */
export const useExcelImport = () => {

    const parseExcelFile = (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject(new Error("No se proporcionó ningún archivo."));
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const dataBinary = e.target.result;

                    // Leer libro
                    const workbook = xlsx.read(dataBinary, { type: 'binary' });
                    const todasLasHojas = [];

                    // Procesar CADA UNA de las hojas del Excel
                    for (const sheetName of workbook.SheetNames) {
                        const sheet = workbook.Sheets[sheetName];
                        const rawData = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: "" });

                        if (!rawData || rawData.length < 3) {
                            // Agregamos hojas vacías solo si el usuario quisiera intentar un escaneo manual en ellas
                            todasLasHojas.push({ sheetName, rawData: [], bloquesEncontrados: [] });
                            continue;
                        }

                        // PASO 1: Identificar cuántos "Bloques" / "Tablas" hay en esta hoja en particular.
                        const bloquesEncontrados = [];

                        for (let i = 0; i < rawData.length; i++) {
                            if (!rawData[i]) continue;
                            const cell0 = String(rawData[i][0]).trim().toLowerCase();
                            if (cell0 === "no" || cell0 === "no.") {
                                let inicioValores = i + 2;

                                let registrosAproximados = 0;
                                for (let j = inicioValores; j < rawData.length; j++) {
                                    if (!rawData[j]) continue;
                                    const posible_id = String(rawData[j][0]).trim();
                                    if (posible_id === "" && String(rawData[j][3]).trim() === "") {
                                        break;
                                    }
                                    if (!isNaN(parseInt(posible_id))) {
                                        registrosAproximados++;
                                    }
                                }

                                if (registrosAproximados > 0) {
                                    bloquesEncontrados.push({
                                        id: (bloquesEncontrados.length + 1).toString(),
                                        filaEncabezado: i, // row index en el arreglo
                                        filaInicioDatos: inicioValores,
                                        registrosEstimados: registrosAproximados
                                    });
                                }
                            }
                        }

                        todasLasHojas.push({
                            sheetName,
                            rawData,
                            bloquesEncontrados
                        });
                    }

                    if (todasLasHojas.length === 0) {
                        throw new Error("El archivo no tiene el formato esperado o fue imposible leer sus hojas.");
                    }

                    // PASO 2: Resolver la información del archivo con todas sus vistas
                    resolve({
                        todasLasHojas
                    });

                } catch (error) {
                    console.error("Error al parear Excel:", error);
                    reject(new Error("El archivo no pudo ser procesado o tiene un formato incorrecto."));
                }
            };

            reader.onerror = () => {
                reject(new Error("Error leyendo el archivo con FileReader."));
            };

            reader.readAsBinaryString(file);
        });
    };

    /**
     * Extrae los datos reales de un bloque/tabla específico que el usuario haya seleccionado.
     */
    const extraeBloqueEspecifico = (rawData, bloqueSeleccionado) => {
        const registrosExtraidos = [];
        const inicio = bloqueSeleccionado.filaInicioDatos;
        const esManual = bloqueSeleccionado.id === 'MANUAL';
        const finRango = esManual ? bloqueSeleccionado.filaFinDatos : rawData.length - 1;

        for (let i = inicio; i <= finRango; i++) {
            const row = rawData[i];

            // Validación estricta para frenar si pasamos la tabla
            if (!row || row.length === 0) continue;

            // Omitir por completo la fila si no tiene número ni como string o un indicador obvio en la base.
            // Ej: una fila suelta que tenga la fecha pero sin el ID "#" en la columna [0].
            const valCol0 = String(row[0]).trim();

            if (valCol0 === "" && String(row[1]).trim() !== "") {
                // Puede ser un registro donde se omitio el consecutivo, entonces forzamos su lectura.
            } else if (valCol0 === "") {
                // Si no hay numero, ni hay fecha, validamos cortes.
                if (!esManual) {
                    if (String(row[3]).trim() === "" && String(row[9]).trim() === "") {
                        break;
                    } else {
                        continue;
                    }
                } else {
                    continue;
                }
            }

            // ===== CORRECCIÓN DE OFFSET DE COLUMNAS =====
            let r_idx = row;

            if (!esManual) {
                // Si el array de la fila se "corrió" porque le faltan columnas previas (Ej. la columna No), lo alineamos:
                let offset_idx = (row[0] === undefined || String(row[0]).trim() === "") ? 1 : 0;
                r_idx = offset_idx === 0 ? row : [null, ...row];
            } else {
                // En modo manual absoluto, la estructura del Array suele no tener desfase de Celdas combinadas 
                // si fue copiado de abajo. Mapeamos exactamente como viene.
                if (isNaN(parseFloat(row[0])) && String(row[0]).length > 5) {
                    // Si [0] es la fecha directa (no copio la primera columna de Numero)
                    r_idx = [null, ...row];
                }
            }

            // Por si el parseador movió los Kms 1 hacia la izquierda saltándose la ClaveBitacora (cuando está vacia)
            let offsetK = 0;
            const isRouteStr = (val) => typeof val === "string" && /[A-Za-z]/.test(val) && val.includes("-");

            if (isRouteStr(r_idx[8]) && !isRouteStr(r_idx[9])) {
                // Ruta cayó en la 8
                offsetK = -1;
            } else if (isRouteStr(r_idx[10]) && !isRouteStr(r_idx[9])) {
                // Ruta cayó en la 10
                offsetK = 1;
            }

            // ===== VALIDACIÓN EXCEPCIONAL PARA FILAS FANTASMA =====
            if (esManual) {
                // Permitimos filas donde falta el ID[0] pero SI HAY una fecha [1] válida o ruta y datos. Ex. la fila 16 de tu imagen.
                const tieneFecha = String(r_idx[1] || "").trim() !== "";
                const tieneRuta = String(r_idx[9 + offsetK] || "").trim() !== "";
                const tieneNumero = !isNaN(parseInt(valCol0)) && valCol0 !== "";
                const tieneBita = String(r_idx[3] || "").trim() !== "";

                if (!tieneFecha && !tieneRuta && !tieneNumero && !tieneBita) {
                    continue; // Es una fila enteramente vacia o basura
                }
            } else {
                // Si llegamos a una zona donde el No es string (ej: "SUBTOTAL", "Total general"), frenamos auto-breaks
                if (isNaN(parseInt(valCol0)) && valCol0 !== "") {
                    if (valCol0.toLowerCase().includes("no") || valCol0.toLowerCase().includes("subtotal") || valCol0.toLowerCase().includes("total")) {
                        break;
                    }
                }
            }

            const parseMonto = (val) => {
                if (val === undefined || val === null || val === '') return 0;
                if (typeof val === 'number') return val;
                const cleanVal = String(val).replace(/[^0-9.-]+/g, "");
                const parsed = parseFloat(cleanVal);
                return isNaN(parsed) ? 0 : parsed;
            };

            const parseEntero = (val) => {
                if (val === undefined || val === null || val === '') return 0;
                if (typeof val === 'number') return val;
                const cleanVal = String(val).replace(/[^0-9.-]+/g, "");
                const parsed = parseInt(cleanVal, 10);
                return isNaN(parsed) ? 0 : parsed;
            };

            const bitacoraObj = {
                // Información Principal
                numero: valCol0 || (registrosExtraidos.length + 1).toString(),
                fechaSalida: String(r_idx[1] || ''),
                fechaCierre: String(r_idx[2] || ''),
                bitacora: String(r_idx[3] || '').trim(),
                cartaPorteFactura: String(r_idx[4] || '').trim(),
                claveBitacora: String(r_idx[5] || '').trim(),
                unidad: String(r_idx[6] || '').trim(),
                dolly: String(r_idx[7] || '').trim(),
                pipa: String(r_idx[8] || '').trim(),
                ruta: String(r_idx[9 + offsetK] || r_idx[9] || '').trim(),

                // Kilómetros y Flete (TotalVal)
                kmsValidadosLyta: parseEntero(r_idx[10 + offsetK]),
                kmsFacturaCryo: parseEntero(r_idx[11 + offsetK]),
                kmsDiferencia: parseEntero(r_idx[12 + offsetK]),
                apoyoKms: parseEntero(r_idx[13 + offsetK]) || String(r_idx[13 + offsetK] || ''), // a veces es num a veces string "CARDENAS"
                fleteCosto: parseMonto(r_idx[14 + offsetK]), // <- COSTO PRINCIPAL DE VIAJE

                // Efectivo
                autopistasEfectivoCasetas: parseEntero(r_idx[16 + offsetK]),
                autopistasEfectivoCosto: parseMonto(r_idx[17 + offsetK]),

                // IAVE / PASE
                autopistasIaveCasetas: parseEntero(r_idx[18 + offsetK]),
                autopistasIaveCosto: parseMonto(r_idx[19 + offsetK]),

                // Total Autopistas (Suma de c/u o usar el que viene en la ref 20, 21)
                totalAutopistasCosto: parseMonto(r_idx[21 + offsetK]),

                // Doble Operador
                dobleOperadorKms: parseEntero(r_idx[22 + offsetK]),
                dobleOperadorCosto: parseMonto(r_idx[23 + offsetK]),

                // Basculas / Pensiones / Renta Dolly / Demoras (Estancias)
                basculasCosto: parseMonto(r_idx[25 + offsetK]),

                // Pensiones
                pensionesNum: parseEntero(r_idx[26 + offsetK]),
                pensionesCosto: parseMonto(r_idx[27 + offsetK]),

                // Renta Dolly
                rentaDollyKms: parseEntero(r_idx[28 + offsetK]),
                rentaDollyCosto: parseMonto(r_idx[29 + offsetK]),

                // Demoras (Asociado a Basculas/Demoras GtosExtraordinarios q el usuario espera en index demoras)
                demorasNum: parseEntero(r_idx[30 + offsetK]),
                demorasCosto: parseMonto(r_idx[31 + offsetK]),
            };

            registrosExtraidos.push(bitacoraObj);
        }
        return registrosExtraidos;
    };

    /**
     * Mapea los registros en crudo del Excel hacia los Conceptos de Formik definidos por el usuario,
     * inyectando las bitácoras en las posiciones correctas según las claves del código de gasto.
     * @param {Array} bitacoras Registros extraídos del Excel.
     * @param {Array} currentConceptos Estado actual de conceptos del formulario.
     * @param {String} fallbackFechaEmision Fecha a utilizar si el Excel falla en proveerla.
     * @returns {Object} { newConceptos, mappedCount }
     */
    const mapearBitacorasAConceptos = (bitacoras, currentConceptos, fallbackFechaEmision) => {
        let mappedCount = 0;

        // Función para asegurar precisión de 2 decimales y evitar errores de flotante
        const redondearDosDecimales = (valor) => Number(parseFloat(valor || 0).toFixed(2));

        // Función para calcular el importe sin IVA (dividido entre 1.16)
        const calcularImporteSinIva = (importe) => importe > 0 ? redondearDosDecimales(importe / 1.16) : 0;

        // Función reutilizable para lidiar con fechas Excel
        const procesarFechaExcel = (fechaSalidaExcel) => {
            let parsedFecha = fechaSalidaExcel;
            const posibleSerial = Number(parsedFecha);
            if (!isNaN(posibleSerial) && posibleSerial > 30000 && posibleSerial < 60000) {
                const dateObj = new Date(Math.round((posibleSerial - 25569) * 86400 * 1000));
                parsedFecha = dateObj.toISOString().split('T')[0];
            } else if (!parsedFecha || String(parsedFecha).trim() === '') {
                parsedFecha = fallbackFechaEmision || new Date().toISOString().split('T')[0];
            } else {
                let rawD = String(parsedFecha).trim().replace(/\//g, '-');
                if (rawD.match(/^\d{2}-\d{2}-\d{2}$/)) { rawD = `20${rawD}`; }
                parsedFecha = rawD;
            }
            return parsedFecha;
        };

        const newConceptos = currentConceptos.map((concepto) => {
            const clonedConcepto = { ...concepto };
            const unparsedClave = clonedConcepto.clave || "";
            const bareStringClave = typeof unparsedClave === 'object' && unparsedClave !== null
                ? (unparsedClave.CodigoGasto || "")
                : String(unparsedClave);
            const clave = bareStringClave.toUpperCase();
            const descripcion = typeof unparsedClave === 'object' && unparsedClave !== null
                ? String(unparsedClave.Descripcion || "").toUpperCase()
                : "";

            if (!clave) return clonedConcepto; // Se ignora si no el usuario no mapeo clave a concepto

            // Iniciamos con un array vacío para que los datos del Excel REEMPLACEN a los existentes (como el placeholder de la factura)
            // en lugar de acumularse. Si el Excel no tiene datos para este concepto, se mantendrán los originales.
            let bitacorasViajeAgregadas = [];

            bitacoras.forEach((bitaEx) => {
                let extractedImporte = 0;
                if ((clave.includes('FLET') || clave.includes('FLD')) && !descripcion.includes('DOLLY')) {
                    extractedImporte = bitaEx.fleteCosto;
                } else if (clave.includes('AUTOP') || clave.includes('CAS') || clave.includes('IAVE')) {
                    // Tomar únicamente el TOTAL (Efectivo + IAVE) como solicitó el usuario, omitiendo desglose individual
                    extractedImporte = bitaEx.totalAutopistasCosto;
                } else if (clave.includes('DOBOP')) {
                    extractedImporte = bitaEx.dobleOperadorCosto;
                } else if (clave.includes('BASCU')) {
                    extractedImporte = bitaEx.basculasCosto;
                } else if (clave.includes('MANIO')) {
                    extractedImporte = bitaEx.demorasCosto;
                } else if (clave.includes('RENT') || clave.includes('DOLLY') || clave.includes('DCA') || descripcion.includes('DOLLY')) {
                    extractedImporte = bitaEx.rentaDollyCosto;
                } else if (clave.includes('PENSI') || clave.includes('ESTAN')) {
                    extractedImporte = bitaEx.pensionesCosto;
                }

                if (extractedImporte > 0 || (clave.includes('FLET') && bitaEx.kmsFacturaCryo > 0)) {
                    const parsedFecha = procesarFechaExcel(bitaEx.fechaSalida);
                    const importeSinIva = calcularImporteSinIva(extractedImporte);
                    const fechaHoy = new Date().toISOString().split('T')[0];

                    bitacorasViajeAgregadas.push({
                        claveBitacora: bitaEx.claveBitacora || "N/A",
                        fecha: parsedFecha,
                        importe: importeSinIva,
                        detalles: [{
                            descripcion: String(bitaEx.ruta || '').trim(),
                            fecha: fechaHoy,
                            importe: importeSinIva
                        }]
                    });
                    mappedCount++;
                }
            });

            if (bitacorasViajeAgregadas.length > 0) {
                clonedConcepto.bitacorasViaje = bitacorasViajeAgregadas;
                clonedConcepto.importe = redondearDosDecimales(
                    bitacorasViajeAgregadas.reduce((a, b) => a + (parseFloat(b.importe) || 0), 0)
                );
            }
            return clonedConcepto;
        });

        return { newConceptos, mappedCount };
    }

    return { parseExcelFile, extraeBloqueEspecifico, mapearBitacorasAConceptos };
};
